import { fmtDesc, plural3 } from './format';
import { clientLine, isTextBlock, lineHas, partHas, textLines, workKg, type ExLookup } from './content';
import type { Block, Line, Maxes } from './types';

/* Тренировка как её видит клиент: блок по строкам (круги — отдельным сегментом) и план строки
   по подходам. Общее для приложения клиента (тренировка, «Итог») и кабинета тренера (просмотр тренировки). */
export type Row =
  | { k: 'text'; text: string }
  | { k: 'line'; line: Line; rounds: number }
  | { k: 'head'; text: string }
  | { k: 'rest'; text: string };

/** Описание настроек комплекса для комментария блока: «3 раунда как можно быстрее, лимит 12:00» */
export const fmtNote = (f: Block['fmt']) => {
  const d = fmtDesc(f);
  return d ? d[0].toUpperCase() + d.slice(1) : '';
};
/** Комментарий блока для показа: свой текст тренера; в старых блоках без него — описание настроек комплекса.
    Формат комплекса отдельной строкой не показываем — он в заголовке, описание — в комментарии (тренер правит) */
export const blockNote = (b: Block) => b.note || fmtNote(b.fmt);

export function blockRows(b: Block): Row[][] {
  const seg: Row[][] = [];
  if (isTextBlock(b)) {
    seg.push(textLines(b.text).map((t) => ({ k: 'text', text: t })));
    return seg;
  }
  let run: Row[] = [];
  const flush = () => {
    if (run.length) seg.push(run);
    run = [];
  };
  for (const y of b.items) {
    if (y.t === 'set') {
      const mem = y.items.filter(lineHas);
      if (!mem.length) continue;
      flush();
      seg.push([
        { k: 'head', text: `${y.rounds} ${plural3(+y.rounds, 'круг', 'круга', 'кругов')}:` },
        ...mem.map((line) => ({ k: 'line' as const, line, rounds: y.rounds })),
        ...(y.rest ? [{ k: 'rest' as const, text: `Отдых ${y.rest} между кругами` }] : []),
      ]);
      continue;
    }
    if (lineHas(y)) run.push({ k: 'line', line: y, rounds: 1 });
  }
  flush();
  return seg;
}


export interface LinePlan {
  text: string;
  sets: number;
  reps: string;
  kg: { lo: number; hi?: number } | null;
  /** единица второго поля: повторы или абсолютная нагрузка (сек, м, кал) */
  unit: string;
  /** есть ли поле веса */
  weighted: boolean;
  chain: boolean;
}

export function linePlan(y: Line, rounds: number, pm: Maxes | null, ex: ExLookup): LinePlan {
  const text = clientLine(y, pm, ex);
  const w = workKg(y, pm, ex);
  const absKg = y.load && y.load.kind === 'abs' && y.load.unit === 'кг' ? { lo: y.load.from, ...(y.load.to != null ? { hi: y.load.to } : {}) } : null;
  const kg = w || absKg;
  const otherUnit = y.load && y.load.kind === 'abs' && y.load.unit !== 'кг' ? y.load : null;
  if (y.t === 'chain') {
    const r = y.parts.filter(partHas).map((p) => p.reps || '1');
    const anyKg = y.parts.some((p) => p.exId && ex(p.exId)?.units.includes('кг'));
    /* Несколько связок в подходе — в плане подхода: «2×(1+1+1)» */
    const rr = (y.per || 0) > 1 ? y.per + '×(' + r.join('+') + ')' : r.join('+');
    return { text, sets: Math.max(1, (y.sets || 1) * rounds), reps: rr, kg, unit: 'повт', weighted: !!kg || anyKg, chain: true };
  }
  let sets = 1;
  let reps = '';
  /* «3-4×10/10» — подходов от 3 до 4: клиент отмечает до четырёх */
  const m = String(y.scheme || '').match(/^\s*(?:\d+\s*[-–]\s*)?(\d+)\s*[x×хХ*]\s*(.*)$/);
  if (m) {
    sets = +m[1];
    reps = m[2].trim();
  } else if (y.scheme) reps = y.scheme.trim();
  sets = Math.max(1, Math.min(30, sets * rounds));
  const e = y.exId ? ex(y.exId) : undefined;
  if (otherUnit) reps = reps || String(otherUnit.from);
  return {
    text,
    sets,
    reps,
    kg,
    unit: otherUnit ? otherUnit.unit : 'повт',
    weighted: !!kg || !!e?.units.includes('кг'),
    chain: false,
  };
}

